/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpPolicySetModule
 */

import {
    IHTTPRewriteURLAction,
    IURIParam,
    IURIParamQuery,
    IURIParamToken,
    URIParamType,
} from 'generated-types';

import {
    isEmpty,
    reduce,
} from 'underscore';

import { HTTPRewriteURLAction } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

import { RepeatedMessageItem }
    from 'ajs/modules/data-model/factories/repeated-message-item.factory';

type THTTPRewriteURLActionPartial = Omit<IHTTPRewriteURLAction, 'host_hdr' | 'path' | 'query'>;

type TURIParamPartial = Omit<IURIParam, 'tokens'>;

/**
 * Interface to update tokens as repeatedMessageItem.
 */
interface IURIParamConfig extends TURIParamPartial {
    tokens: RepeatedMessageItem<MessageItem<IURIParamToken>>,
}

/**
 * Extended IHTTPRewriteURLAction Interface to add UI only fields.
 */
interface IHTTPRewriteURLActionConfig extends THTTPRewriteURLActionPartial {
    host_hdr?: MessageItem<IURIParamConfig>,
    path?: MessageItem<IURIParamConfig>,
    query?: MessageItem<IURIParamQuery>,
    hostStr?: string,
    pathStr?: string,
    paramQuery?: IURIParamQuery
}

/**
 * @description HTTPRewriteURLAction ConfigItem.
 * @author Abhinesh Gour, Umesh Chovatiya
 */
export class HTTPRewriteURLActionConfigItem extends MessageItem<IHTTPRewriteURLActionConfig> {
    public static ajsDependencies = [
        'RangeParser',
    ];

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPRewriteURLAction,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Convert host_hdr, path and query tokens to string values required on UI.
     */
    /** @override */
    public modifyConfigDataAfterLoad(): void {
        const { host_hdr: hostHdr, path, query } = this.config;

        super.modifyConfigDataAfterLoad();

        if (!hostHdr) {
            this.safeSetNewChildByField('host_hdr');
        } else if (hostHdr && hostHdr.config.tokens) {
            this.config.hostStr = this.extractStringValue(hostHdr.config.tokens, '.');
        }

        if (!path) {
            this.safeSetNewChildByField('path');
        } else if (path && path.config.tokens) {
            const value = this.extractStringValue(path.config.tokens, '/');

            this.config.pathStr = !isEmpty(value) && value || '';
        }

        if (query) {
            this.config.paramQuery = query.data.config;
        } else {
            this.safeSetNewChildByField('query');
            this.config.paramQuery = {
                keep_query: false,
            };
        }
    }

    /**
     * Extracts value from the config and prepares a string to show action details in the grid
     * expander for action.
     */
    public actionReadonlyDetails(): string {
        const { hostStr, pathStr } = this.config;

        const readOnlyUrl = `${hostStr}/${pathStr}`;

        return readOnlyUrl;
    }

    /**
     * Convert host and path string values to tokenised params.
     */
    /** @override */
    public modifyConfigDataBeforeSave(): void {
        const { hostStr, pathStr, paramQuery } = this.config;

        const rangeParser = this.getAjsDependency_('RangeParser');

        super.modifyConfigDataBeforeSave();

        if (hostStr) {
            this.config.host_hdr.updateConfig({
                type: URIParamType.URI_PARAM_TYPE_TOKENIZED,
                tokens: rangeParser.tokensStr2tokens(hostStr, '.'),
            });

            delete this.config.hostStr;
        } else {
            delete this.config.host_hdr;
        }

        if (pathStr) {
            this.config.path.updateConfig({
                type: URIParamType.URI_PARAM_TYPE_TOKENIZED,
                tokens: rangeParser.tokensStr2tokens(pathStr, '/'),
            });

            delete this.config.pathStr;
        } else {
            delete this.config.path;
        }

        if (paramQuery) {
            const queryParams: IURIParamQuery = {
                keep_query: paramQuery.keep_query,
            };

            if (paramQuery.keep_query) {
                queryParams.add_string = paramQuery.add_string;
            }

            this.config.query.updateConfig(queryParams);

            delete this.config.paramQuery;
        }
    }

    /**
     * Convert host and path tokens to formatted string value.
     */
    private extractStringValue(
        tokens: RepeatedMessageItem<MessageItem<IURIParamToken>>,
        separator: string,
    ): string {
        const rangeParser = this.getAjsDependency_('RangeParser');

        return reduce(tokens.config,
            (base, token) => `${base + rangeParser.token2str(token.config)}${separator}`,
            '').slice(0, -1);
    }
}

